import { Component, OnInit, Output, EventEmitter, Inject } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { LoadingService } from "src/app/ui/services/loading.service";
import { AlertService } from "src/app/ui/services/alert.service";
import { FormDirty } from "src/app/ui/models/utils/form-dirty.utils";
import { ContactValidator } from "../validators/contact-validator";
import { CompanyContactHandler } from "src/app/ui/models/domains/pbm/companies/company-contact-handler";
import { CompanyService } from "src/app/ui/services/pbm/company.service";
import { CompanyContact } from "src/app/ui/models/domains/pbm/companies/company-contact";
import { MaskInput } from "src/app/ui/models/domains/masks/mask.model";
import { UserPermissions } from "./../../../../models/domains/pbm/user/user-permissions";
import { UserService } from "src/app/ui/services/user.service";
import { JobDescriptionService } from "src/app/ui/services/pbm/job-description.service";
import { JobDescription } from "src/app/ui/models/domains/pbm/Beneficiary/job-description";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

@Component({
  selector: "company-contact-dialog",
  templateUrl: "./company-contact-dialog.component.html",
  styleUrls: ["./company-contact-dialog.component.scss"],
})
export class CompanyContactDialogComponent implements OnInit {
  @Output() saveContact: EventEmitter<CompanyContact> = new EventEmitter();
  @Output() editContact: EventEmitter<CompanyContact> = new EventEmitter();

  private formDirty: FormDirty;
  private jobDescriptionList: JobDescription[];

  telephoneMask = MaskInput.getTelephoneMask();
  mobileMask = MaskInput.getMobileMask();
  federalRegistrationMask = MaskInput.getFederalRegistrationMask();
  socialNumberMask = MaskInput.getSocialNumberMask();

  public companyContactForm: UntypedFormGroup = this.formBuilder.group({
    companyContactKey: [""],
    companyFiscalNumber: [""],
    name: [""],
    unit: [""],
    jobDescription: [""],
    telephone: [""],
    mobile: [""],
    email: [""],
    area: [""],
    additionalInformation: [""],
    hasLegalRepresentativeInfo: [false],
    federalRegistration: [""],
    socialNumber: [""],
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CompanyContact,
    private formBuilder: UntypedFormBuilder,
    private dialogRef: MatDialogRef<any>,
    private loadingService: LoadingService,
    private alertService: AlertService,
    private companyService: CompanyService,
    private userService: UserService,
    private jobDescriptionService: JobDescriptionService,
  ) {
    this.formDirty = new FormDirty();
  }

  async ngOnInit() {
    const ref = this.loadingService.beginLoading();
    this.companyContactForm.patchValue({ companyFiscalNumber: this.data.getCompanyFiscalNumber() });
    this.jobDescriptionList = await this.jobDescriptionService.getJobDescription(this.data.getCompanyFiscalNumber());
    if (this.data.getCompanyContactKey() !== "") this.patchForm(this.data);
    this.formDirty.markAsClean();
    this.loadingService.finishLoading(ref);
  }

  patchForm(companyContact: CompanyContact): void {
    const companyContacHandler = new CompanyContactHandler(this.companyContactForm);
    companyContacHandler.handleCompanyContactToForm(companyContact, this.jobDescriptionList);
  }

  async onSaveContact() {
    const ref = this.loadingService.beginLoading();
    const contactValidator = new ContactValidator();

    if (contactValidator.validate(this.companyContactForm).hasErrorMessages()) {
      this.showErrorMessage(contactValidator.getValidationResult().getErrorMessage());
      this.loadingService.finishLoading(ref);
    } else {
      const companyContacHandler = new CompanyContactHandler(this.companyContactForm);
      const companyContact = companyContacHandler.handleFormToCompanyContact();
      try {
        await this.companyService.addCompanyContact(companyContact);
        this.saveContact.emit();
        this.formDirty.markAsClean();
        this.close();
      } catch (error) {
        this.showErrorMessage(error);
      } finally {
        this.loadingService.finishLoading(ref);
      }
    }
  }

  async onEditContact() {
    const ref = this.loadingService.beginLoading();

    const contactValidator = new ContactValidator();

    if (contactValidator.validate(this.companyContactForm).hasErrorMessages()) {
      this.showErrorMessage(contactValidator.getValidationResult().getErrorMessage());

      this.loadingService.finishLoading(ref);
    } else {
      const companyContacHandler = new CompanyContactHandler(this.companyContactForm);

      const companyContact = companyContacHandler.handleFormToCompanyContact();

      try {
        await this.companyService.editCompanyContact(companyContact);

        this.editContact.emit();

        this.formDirty.markAsClean();

        this.close();
      } catch (error) {
        this.showErrorMessage(error);
      } finally {
        this.loadingService.finishLoading(ref);
      }
    }
  }

  showErrorMessage(errorMessage: string): void {
    this.alertService.onlyOneError(errorMessage, true, "alert-modal");
  }

  onContactInputChange(): void {
    this.formDirty.markAsDirty();
  }

  isNewContact(): boolean {
    return !this.data.getCompanyContactKey();
  }

  markAsDirty(): void {
    this.formDirty.markAsDirty();
  }

  close(): void {
    if (this.formDirty.isDirty()) {
      const exitWithoutSave = confirm("Os dados foram alterados no formulário. Deseja sair sem salvar?");

      if (exitWithoutSave) this.dialogRef.close();
    } else this.dialogRef.close();
  }

  hasAccessToEditCompany(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.EditCompany);
  }

  public getJobDescriptionList(): JobDescription[] {
    return this.jobDescriptionList;
  }

  hasMobile(): boolean {
    return !!this.companyContactForm.get("mobile").value;
  }

  hasTelephone(): boolean {
    return !!this.companyContactForm.get("telephone").value;
  }
}
